import React, { useEffect, useState } from "react";
import { Loader2, RefreshCw, Save, ExternalLink, Check, AlertTriangle, X } from "lucide-react";
import { fetchRestaurant, updateRestaurant, refreshReviewPlatforms } from "../../lib/api";
import { toast } from "../../hooks/use-toast";

const SCALE_OPTIONS = [
  { value: 5, label: "/5 (stelle)" },
  { value: 10, label: "/10 (numerico)" },
];

export default function ExternalReviewsEditor() {
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [statuses, setStatuses] = useState({}); // {platformId: {ok, error}}

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const r = await fetchRestaurant();
      setPlatforms(r.externalReviews || []);
    } finally {
      setLoading(false);
    }
  }

  function setField(id, key, value) {
    setPlatforms((prev) => prev.map((p) => (p.id === id ? { ...p, [key]: value } : p)));
  }

  async function save() {
    setSaving(true);
    try {
      await updateRestaurant({ externalReviews: platforms });
      toast({ title: "Salvato", description: "Recensioni aggiornate" });
    } catch (e) {
      toast({ title: "Errore", description: "Salvataggio fallito", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  async function doRefresh() {
    setRefreshing(true);
    setStatuses({});
    try {
      const res = await refreshReviewPlatforms();
      const map = {};
      (res.results || []).forEach((r) => {
        map[r.id] = { ok: !!r.ok, error: r.error || "" };
      });
      setStatuses(map);
      setPlatforms(res.platforms || []);
      const okCount = (res.results || []).filter((r) => r.ok).length;
      const totCount = (res.results || []).length;
      toast({
        title: `${okCount}/${totCount} piattaforme aggiornate`,
        description: okCount < totCount ? "Le altre vanno aggiornate manualmente qui sotto" : "Tutto fresco!",
      });
    } catch (e) {
      toast({ title: "Errore", description: "Refresh fallito", variant: "destructive" });
    } finally {
      setRefreshing(false);
    }
  }

  if (loading) {
    return <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#4a1c17]" /></div>;
  }

  return (
    <div data-testid="external-reviews-editor" className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div>
          <div className="text-sm text-[#2b1410]">
            Punteggi e link delle piattaforme mostrati nel menu pubblico. Aggiorna manualmente oppure prova il refresh automatico.
          </div>
          <div className="text-xs text-[#7c5a4e] mt-1">
            ⚠️ Il refresh automatico può fallire su alcune piattaforme (blocchi anti-bot). In quel caso aggiorna manualmente il campo qui sotto.
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={doRefresh}
            disabled={refreshing}
            data-testid="refresh-platforms-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#c48b31] hover:bg-[#a67728] text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
          >
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Aggiorna da web
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="save-platforms-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#4a1c17] hover:bg-[#6a2c22] text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Salva
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {platforms.map((p) => {
          const st = statuses[p.id];
          return (
            <div
              key={p.id}
              data-testid={`platform-row-${p.id}`}
              className="rounded-xl border border-[#e8dcc8] bg-white p-4"
            >
              <div className="flex items-center gap-3 mb-3">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={p.enabled !== false}
                    onChange={(e) => setField(p.id, "enabled", e.target.checked)}
                    className="h-4 w-4 accent-[#4a1c17]"
                  />
                  <span className="text-sm font-semibold text-[#2b1410]">{p.label}</span>
                </label>
                {st?.ok && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                    <Check className="h-3 w-3" /> Aggiornato da web
                  </span>
                )}
                {st && !st.ok && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full" title={st.error}>
                    <AlertTriangle className="h-3 w-3" /> Refresh fallito · edit manuale
                  </span>
                )}
                {p.url && (
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-auto text-xs font-medium text-[#4a1c17] hover:underline inline-flex items-center gap-1"
                  >
                    Apri <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
                <div className="sm:col-span-3">
                  <label className="block text-[11px] font-medium text-[#7c5a4e] mb-0.5">Punteggio</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max={p.scale || 5}
                    value={p.rating ?? 0}
                    onChange={(e) => setField(p.id, "rating", parseFloat(e.target.value || 0))}
                    className="w-full rounded-lg border border-[#e8dcc8] px-3 py-1.5 text-sm"
                    data-testid={`rating-input-${p.id}`}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[11px] font-medium text-[#7c5a4e] mb-0.5">Scala</label>
                  <select
                    value={p.scale || 5}
                    onChange={(e) => setField(p.id, "scale", parseInt(e.target.value))}
                    className="w-full rounded-lg border border-[#e8dcc8] px-3 py-1.5 text-sm bg-white"
                  >
                    {SCALE_OPTIONS.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[11px] font-medium text-[#7c5a4e] mb-0.5">N° recensioni</label>
                  <input
                    type="number"
                    min="0"
                    value={p.count ?? 0}
                    onChange={(e) => setField(p.id, "count", parseInt(e.target.value || 0))}
                    className="w-full rounded-lg border border-[#e8dcc8] px-3 py-1.5 text-sm"
                    data-testid={`count-input-${p.id}`}
                  />
                </div>
                <div className="sm:col-span-5">
                  <label className="block text-[11px] font-medium text-[#7c5a4e] mb-0.5">URL</label>
                  <input
                    type="url"
                    value={p.url || ""}
                    onChange={(e) => setField(p.id, "url", e.target.value)}
                    placeholder="https://..."
                    className="w-full rounded-lg border border-[#e8dcc8] px-3 py-1.5 text-sm"
                    data-testid={`url-input-${p.id}`}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
